import { adminAc, defaultAc, userAc } from "better-auth/plugins/admin/access";

/**
 * Better Auth's `adminAc` grants `user: ["impersonate"]` but DELIBERATELY omits
 * `"impersonate-admins"`, which its impersonate route requires whenever the
 * target's role is in `adminRoles` — so a full admin was refused outright with
 * "You cannot impersonate admins" when viewing as another admin. That was never
 * intended (`docs/view-as-impersonation-design.md` claimed it already worked),
 * and HR asked for it on 2026-09-22.
 *
 * Granted as a PERMISSION rather than via the plugin's `allowImpersonatingAdmins`
 * flag, which Better Auth marks `@deprecated` in favour of exactly this.
 *
 * Built with `defaultAc.newRole` so it stays one of Better Auth's own access
 * objects — see the `ac` note above; a hand-rolled `{ authorize, statements }`
 * would work at runtime and break the inferred role union.
 *
 * Note this widens only `admin`. `admin_readonly` was already impersonable, and
 * still is, because `adminRoles` is `["admin"]` alone — the check never fires
 * for it.
 */
const adminWithImpersonateAdmins = defaultAc.newRole({
  ...adminAc.statements,
  user: [...adminAc.statements.user, "impersonate-admins"],
});

/**
 * Better Auth role declarations, shared by the server (`auth.ts`) and the client
 * (`auth-client.ts`).
 *
 * They MUST be the same object on both sides: each plugin infers the role union
 * it will accept from `keyof roles`, defaulting to just `"user" | "admin"` when
 * unset — which rejected `setRole("manager")` outright.
 *
 * **Supplying `roles` switches the plugin from the `adminRoles` shortcut to
 * permission-based authorization.** That is why every tier is declared with one
 * of Better Auth's own access objects rather than something hand-rolled: a role
 * with no statements denies the admin API to *everyone*, including a full admin
 * (it surfaced as `YOU_ARE_NOT_ALLOWED_TO_CHANGE_USERS_ROLE` on three tests).
 *
 * This is NOT where Enable's own permissions live — capabilities are resolved in
 * `src/lib/permissions.ts`. These declarations govern only Better Auth's account
 * management endpoints.
 *
 * No `ac` option is passed alongside these: they are already built from Better
 * Auth's own `defaultAc`, and passing that object as `ac` does not satisfy the
 * plugin's `AccessControl` type — which silently collapsed the inferred role
 * union back to "user" | "admin" on both sides.
 */
export const appRoles = {
  /** No account-management permissions (`userAc` is `{ user: [], session: [] }`). */
  user: userAc,
  /** A manager manages people in Enable, not accounts — same as `user` here. */
  manager: userAc,
  /**
   * The load-bearing line of this whole feature: a view-only admin gets the
   * PLAIN USER permission set, so it cannot create, ban, delete, impersonate or
   * re-role an account. Combined with `adminRoles: ["admin"]`, that is denied
   * twice over rather than relying on us remembering to gate each route.
   */
  admin_readonly: userAc,
  /** Better Auth's default admin set, plus the one statement it withholds. */
  admin: adminWithImpersonateAdmins,
};
